// ============================================
// Therapist Profile
// ============================================

import React from 'react';
import { doc, updateDoc, collection, getDocs, getDoc, query, where, serverTimestamp, setDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { db, storage, functions, auth } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { PhotoStatus, EventStatus, Session, Settlement } from '../../types';

interface SessionsByDay {
  date: string;
  dateLabel: string;
  sessions: Session[];
  totalMinutes: number;
  totalBillPayout: number;
  totalTips: number;
  totalEarnings: number;
  totalAmountReceived: number;  // Cash collected by therapist
  totalBillAmount: number;      // Total bill for sessions
  eventPayout: number;          // Amount owed to event
}

interface MyEvent {
  id: string;
  name: string;
  venueName: string;
  status: EventStatus;
  startDate?: string;
  endDate?: string;
}

export default function TherapistProfile() {
  const { user, userData } = useAuth();
  const [displayName, setDisplayName] = React.useState(userData?.displayName || '');
  const [publicDisplayName, setPublicDisplayName] = React.useState('');
  const [photoFile, setPhotoFile] = React.useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = React.useState<string | null>(userData?.photoUrl || null);
  const [photoStatus, setPhotoStatus] = React.useState<PhotoStatus | undefined>(undefined);
  const [defaultBookingMinutes, setDefaultBookingMinutes] = React.useState<number | ''>('');
  const [eventDefaultMinutes, setEventDefaultMinutes] = React.useState<number>(30);
  const [loading, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Invite code section
  const [inviteCode, setInviteCode] = React.useState('');
  const [inviteLoading, setInviteLoading] = React.useState(false);
  const [inviteError, setInviteError] = React.useState<string | null>(null);
  const [inviteSuccess, setInviteSuccess] = React.useState(false);

  // Events I'm part of
  const [myEvents, setMyEvents] = React.useState<MyEvent[]>([]);
  const [eventsLoading, setEventsLoading] = React.useState(true);

  // Find current event for updating therapist profile
  const [currentEventId, setCurrentEventId] = React.useState<string | null>(null);

  // Sessions history
  const [showSessions, setShowSessions] = React.useState(false);
  const [sessionsByDay, setSessionsByDay] = React.useState<SessionsByDay[]>([]);
  const [sessionsLoading, setSessionsLoading] = React.useState(false);
  const [expandedDay, setExpandedDay] = React.useState<string | null>(null);

  // Settlements
  const [settlements, setSettlements] = React.useState<Record<string, Settlement>>({});
  const [settlingDay, setSettlingDay] = React.useState<string | null>(null);

  // Find all events where user is a therapist
  React.useEffect(() => {
    if (!user) return;

    const findEvents = async () => {
      try {
        const foundEvents: MyEvent[] = [];
        let firstLiveEventId: string | null = null;

        // Query live events (therapists can read live events)
        const liveEventsQuery = query(collection(db, 'events'), where('status', '==', 'live'));
        const liveEventsSnap = await getDocs(liveEventsQuery);

        for (const eventDoc of liveEventsSnap.docs) {
          try {
            const therapistDoc = await getDoc(doc(db, 'events', eventDoc.id, 'therapists', user.uid));
            if (therapistDoc.exists()) {
              const eventData = eventDoc.data();
              foundEvents.push({
                id: eventDoc.id,
                name: eventData.name,
                venueName: eventData.venueName,
                status: 'live',
                startDate: eventData.startDate,
                endDate: eventData.endDate,
              });

              // Use first live event for profile settings
              if (!firstLiveEventId) {
                firstLiveEventId = eventDoc.id;
                const therapistData = therapistDoc.data();
                setDisplayName(therapistData.displayName || userData?.displayName || '');
                setPublicDisplayName(therapistData.publicDisplayName || '');
                setPhotoPreview(therapistData.originalPhotoUrl || therapistData.photoUrl || null);
                setPhotoStatus(therapistData.photoStatus);
                setDefaultBookingMinutes(therapistData.defaultBookingMinutes || '');
                setEventDefaultMinutes(eventData.defaultBookingMinutes || 30);
              }
            }
          } catch (err) {
            console.log('No access to therapist data in event:', eventDoc.id);
          }
        }

        // Also check closed events for history
        try {
          const closedEventsQuery = query(collection(db, 'events'), where('status', '==', 'closed'));
          const closedEventsSnap = await getDocs(closedEventsQuery);

          for (const eventDoc of closedEventsSnap.docs) {
            try {
              const therapistDoc = await getDoc(doc(db, 'events', eventDoc.id, 'therapists', user.uid));
              if (therapistDoc.exists()) {
                const eventData = eventDoc.data();
                foundEvents.push({
                  id: eventDoc.id,
                  name: eventData.name,
                  venueName: eventData.venueName,
                  status: 'closed',
                  startDate: eventData.startDate,
                  endDate: eventData.endDate,
                });
              }
            } catch (err) {
              // Skip events where we don't have access
            }
          }
        } catch (err) {
          // Can't read closed events, that's ok
        }

        setCurrentEventId(firstLiveEventId);
        setMyEvents(foundEvents);
      } catch (err) {
        console.error('Error finding events:', err);
      } finally {
        setEventsLoading(false);
      }
    };

    findEvents();
  }, [user, userData]);

  // Load sessions when button clicked
  const loadSessions = async () => {
    if (!user || myEvents.length === 0) return;

    setSessionsLoading(true);
    try {
      const allSessions: Session[] = [];

      // Load sessions from all events
      for (const event of myEvents) {
        try {
          // Simple query - just filter by therapistId, do rest client-side
          const sessionsQuery = query(
            collection(db, 'events', event.id, 'sessions'),
            where('therapistId', '==', user.uid)
          );
          const sessionsSnap = await getDocs(sessionsQuery);
          sessionsSnap.docs.forEach((docSnap) => {
            const data = docSnap.data() as Session;
            // Filter completed sessions client-side
            if (data.status === 'completed') {
              allSessions.push({ ...data, id: docSnap.id });
            }
          });
        } catch (err) {
          console.log('Could not load sessions from event:', event.id, err);
        }
      }

      // Group sessions by day
      const grouped: Record<string, Session[]> = {};
      allSessions.forEach((session) => {
        const date = session.startedAt?.toDate?.()
          ? session.startedAt.toDate().toISOString().split('T')[0]
          : 'unknown';
        if (!grouped[date]) {
          grouped[date] = [];
        }
        grouped[date].push(session);
      });

      // Convert to array and calculate totals
      const days: SessionsByDay[] = Object.entries(grouped)
        .map(([date, sessions]) => {
          // Sort sessions by start time (most recent first within day)
          const sortedSessions = sessions.sort((a, b) => {
            const aTime = a.startedAt?.toDate?.()?.getTime() || 0;
            const bTime = b.startedAt?.toDate?.()?.getTime() || 0;
            return bTime - aTime;
          });
          const totalMinutes = sortedSessions.reduce((sum, s) => sum + (s.actualMinutes || s.totalMinutes || 0), 0);
          const totalBillPayout = sortedSessions.reduce((sum, s) => sum + (s.therapistBillPayout || 0), 0);
          const totalTips = sortedSessions.reduce((sum, s) => sum + (s.therapistTipPayout || 0), 0);
          const totalEarnings = sortedSessions.reduce((sum, s) => sum + (s.therapistTotalPayout || 0), 0);
          const totalAmountReceived = sortedSessions.reduce((sum, s) => sum + (s.amountReceived || 0), 0);
          const totalBillAmount = sortedSessions.reduce((sum, s) => sum + (s.billAmount || 0), 0);
          const eventPayout = sortedSessions.reduce((sum, s) => sum + (s.eventPayout || 0), 0);
          const dateObj = new Date(date + 'T12:00:00');
          return {
            date,
            dateLabel: dateObj.toLocaleDateString('en-US', {
              weekday: 'short',
              month: 'short',
              day: 'numeric',
            }),
            sessions: sortedSessions,
            totalMinutes,
            totalBillPayout,
            totalTips,
            totalEarnings,
            totalAmountReceived,
            totalBillAmount,
            eventPayout,
          };
        })
        .sort((a, b) => b.date.localeCompare(a.date)); // Most recent day first

      setSessionsByDay(days);

      // Load existing settlements for this therapist
      if (currentEventId && user) {
        const settlementsQuery = query(
          collection(db, 'events', currentEventId, 'settlements'),
          where('therapistId', '==', user.uid)
        );
        const settlementsSnap = await getDocs(settlementsQuery);
        const settlementsMap: Record<string, Settlement> = {};
        settlementsSnap.docs.forEach((docSnap) => {
          const data = docSnap.data() as Settlement;
          settlementsMap[data.date] = { ...data, id: docSnap.id };
        });
        setSettlements(settlementsMap);
      }
    } catch (err) {
      console.error('Error loading sessions:', err);
    } finally {
      setSessionsLoading(false);
    }
  };

  // Create settlement for a day
  const createSettlement = async (day: SessionsByDay) => {
    if (!user || !currentEventId) return;

    setSettlingDay(day.date);
    try {
      const settlementId = `${user.uid}_${day.date}`;
      const settlementRef = doc(db, 'events', currentEventId, 'settlements', settlementId);

      const settlement: Omit<Settlement, 'id'> = {
        therapistId: user.uid,
        therapistName: displayName || user.displayName || 'Unknown',
        date: day.date,
        totalSessions: day.sessions.length,
        totalMinutes: day.totalMinutes,
        totalAmountReceived: day.totalAmountReceived,
        totalBillAmount: day.totalBillAmount,
        therapistPayout: day.totalEarnings,
        eventPayout: day.eventPayout,
        totalTips: day.totalTips,
        status: 'pending',
        createdAt: serverTimestamp() as any,
        createdBy: user.uid,
      };

      await setDoc(settlementRef, settlement);

      // Update local state
      setSettlements((prev) => ({
        ...prev,
        [day.date]: { ...settlement, id: settlementId } as Settlement,
      }));
    } catch (err) {
      console.error('Error creating settlement:', err);
      alert('Failed to create settlement. Please try again.');
    } finally {
      setSettlingDay(null);
    }
  };

  // Handle invite code submission
  const handleRedeemCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteCode.trim()) {
      setInviteError('Please enter an invite code');
      return;
    }

    setInviteLoading(true);
    setInviteError(null);

    try {
      const redeemInviteCode = httpsCallable(functions, 'redeemInviteCode');
      await redeemInviteCode({
        code: inviteCode.trim().toUpperCase(),
      });

      setInviteSuccess(true);
      setInviteCode('');

      // Force token refresh to get new role claims
      if (auth.currentUser) {
        await auth.currentUser.getIdToken(true);
      }

      // Refresh the page to show new event
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (err: any) {
      console.error('Error redeeming code:', err);
      if (err.message?.includes('not authorized')) {
        setInviteError('Your email address is not authorized for this invite. Contact the organizer.');
      } else {
        setInviteError(err.message || 'Invalid or expired invite code');
      }
    } finally {
      setInviteLoading(false);
    }
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setError('Image must be less than 2MB');
      return;
    }

    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setSaving(true);
    setError(null);
    setSaved(false);

    try {
      let originalPhotoUrl: string | undefined = undefined;

      // Upload photo if changed - goes to original.jpg for admin approval
      if (photoFile) {
        const photoRef = ref(storage, `therapists/${user.uid}/original.jpg`);
        await uploadBytes(photoRef, photoFile);
        originalPhotoUrl = await getDownloadURL(photoRef);
      }

      // Update user document
      await updateDoc(doc(db, 'users', user.uid), {
        displayName,
      });

      // Update event therapist membership if exists
      if (currentEventId) {
        const therapistUpdate: Record<string, any> = {
          displayName,
        };

        // Handle public display name - empty string means use real name
        if (publicDisplayName.trim()) {
          therapistUpdate.publicDisplayName = publicDisplayName.trim();
        } else {
          therapistUpdate.publicDisplayName = null;
        }

        // If new photo uploaded, set it as pending approval
        if (originalPhotoUrl) {
          therapistUpdate.originalPhotoUrl = originalPhotoUrl;
          therapistUpdate.photoStatus = 'pending';
          therapistUpdate.photoUploadedAt = serverTimestamp();
          // Don't update photoUrl - that's set by admin after approval
        }

        // Handle default booking minutes - empty string means use event default
        if (defaultBookingMinutes === '' || defaultBookingMinutes === null) {
          therapistUpdate.defaultBookingMinutes = null;
        } else {
          therapistUpdate.defaultBookingMinutes = Number(defaultBookingMinutes);
        }

        await updateDoc(doc(db, 'events', currentEventId, 'therapists', user.uid), therapistUpdate);

        if (originalPhotoUrl) {
          setPhotoStatus('pending');
        }
      }

      setSaved(true);
    } catch (err: any) {
      console.error('Error saving profile:', err);
      setError(err.message || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="therapist-profile">
      <h1>Your Profile</h1>

      <form onSubmit={handleSubmit} className="profile-form">
        {error && <div className="error-message">{error}</div>}
        {saved && <div className="success-message">Profile saved!</div>}

        {/* Photo Upload */}
        <div className="photo-section">
          <div className="photo-preview" style={{ position: 'relative' }}>
            {photoPreview ? (
              <img src={photoPreview} alt="Profile" />
            ) : (
              <div className="photo-placeholder">
                {displayName.charAt(0) || '?'}
              </div>
            )}
            {photoStatus && (
              <div
                className={`photo-status-badge ${photoStatus}`}
                style={{
                  position: 'absolute',
                  bottom: '-8px',
                  right: '-8px',
                  padding: '4px 8px',
                  borderRadius: '12px',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  backgroundColor:
                    photoStatus === 'approved' ? '#22c55e' :
                    photoStatus === 'pending' ? '#f59e0b' :
                    '#ef4444',
                  color: '#fff',
                }}
              >
                {photoStatus}
              </div>
            )}
          </div>
          <label className="photo-upload-btn">
            <input
              type="file"
              accept="image/*"
              onChange={handlePhotoChange}
              style={{ display: 'none' }}
            />
            <span className="btn btn-secondary">Change Photo</span>
          </label>
          <p className="photo-hint">Portrait photo (1:3 ratio preferred), max 2MB</p>
          {photoStatus === 'pending' && (
            <p style={{ color: '#f59e0b', fontSize: '0.875rem', marginTop: '0.5rem' }}>
              Your photo is pending admin approval
            </p>
          )}
          {photoStatus === 'rejected' && (
            <p style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.5rem' }}>
              Your photo was rejected. Please upload a new one.
            </p>
          )}
        </div>

        {/* Real Name */}
        <div className="form-group">
          <label htmlFor="displayName">Real Name</label>
          <input
            type="text"
            id="displayName"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Your real name"
            required
          />
          <p className="hint">Your real name (visible to admins only)</p>
        </div>

        {/* Public Display Name */}
        <div className="form-group">
          <label htmlFor="publicDisplayName">Public Display Name (optional)</label>
          <input
            type="text"
            id="publicDisplayName"
            value={publicDisplayName}
            onChange={(e) => setPublicDisplayName(e.target.value)}
            placeholder={displayName || 'Leave empty to use real name'}
          />
          <p className="hint">Name shown to players. Leave empty to use your real name.</p>
        </div>

        {/* Email (read-only) */}
        <div className="form-group">
          <label>Email</label>
          <input type="email" value={user?.email || ''} disabled />
        </div>

        {/* Default Booking Duration */}
        {currentEventId && (
          <div className="form-group">
            <label htmlFor="defaultBookingMinutes">Default Booking Duration (minutes)</label>
            <input
              type="number"
              id="defaultBookingMinutes"
              value={defaultBookingMinutes}
              onChange={(e) => setDefaultBookingMinutes(e.target.value === '' ? '' : Number(e.target.value))}
              placeholder={`Event default: ${eventDefaultMinutes}`}
              min="5"
              step="5"
            />
            <p className="hint">
              Leave empty to use event default ({eventDefaultMinutes} min). Set your own if you prefer a different starting duration.
            </p>
          </div>
        )}

        <button type="submit" className="btn btn-primary btn-large" disabled={loading}>
          {loading ? 'Saving...' : 'Save Profile'}
        </button>
      </form>

      {/* My Events Section */}
      <section style={{ marginTop: '2rem' }}>
        <h2 style={{ marginBottom: '1rem', fontSize: '1.25rem' }}>My Events</h2>
        {eventsLoading ? (
          <p style={{ color: 'var(--color-text-secondary)' }}>Loading events...</p>
        ) : myEvents.length === 0 ? (
          <p style={{ color: 'var(--color-text-secondary)' }}>You haven't joined any events yet.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {myEvents.map((event) => (
              <div
                key={event.id}
                style={{
                  padding: '1rem',
                  borderRadius: '8px',
                  backgroundColor: 'var(--color-bg-secondary)',
                  border: event.status === 'live' ? '2px solid var(--color-primary)' : '1px solid var(--color-border)',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>{event.name}</div>
                    <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
                      {event.venueName}
                    </div>
                    {event.startDate && event.endDate && (
                      <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)', marginTop: '0.25rem' }}>
                        {new Date(event.startDate).toLocaleDateString()} - {new Date(event.endDate).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                  <span
                    style={{
                      padding: '0.25rem 0.5rem',
                      borderRadius: '4px',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      backgroundColor: event.status === 'live' ? 'var(--color-primary)' : 'var(--color-text-secondary)',
                      color: '#fff',
                    }}
                  >
                    {event.status === 'live' ? 'Active' : 'Closed'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* My Sessions Section */}
      <section style={{ marginTop: '2rem', paddingTop: '1.5rem', borderTop: '1px solid var(--color-border)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2 style={{ fontSize: '1.25rem', margin: 0 }}>My Sessions</h2>
          {!showSessions ? (
            <button
              onClick={() => {
                setShowSessions(true);
                loadSessions();
              }}
              className="btn btn-secondary btn-sm"
            >
              View History
            </button>
          ) : (
            <button
              onClick={() => setShowSessions(false)}
              className="btn btn-sm"
              style={{ background: 'transparent', color: 'var(--color-text-secondary)' }}
            >
              Hide
            </button>
          )}
        </div>

        {showSessions && (
          <>
            {sessionsLoading ? (
              <p style={{ color: 'var(--color-text-secondary)' }}>Loading sessions...</p>
            ) : sessionsByDay.length === 0 ? (
              <p style={{ color: 'var(--color-text-secondary)' }}>No completed sessions yet.</p>
            ) : (
              <>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {sessionsByDay.map((day) => (
                    <div
                      key={day.date}
                      style={{
                        borderRadius: '8px',
                        backgroundColor: 'var(--color-bg-secondary)',
                        border: '1px solid var(--color-border)',
                        overflow: 'hidden',
                      }}
                    >
                      {/* Day Header - Clickable */}
                      <button
                        onClick={() => setExpandedDay(expandedDay === day.date ? null : day.date)}
                        style={{
                          width: '100%',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '0.75rem 1rem',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          textAlign: 'left',
                        }}
                      >
                        <div>
                          <div style={{ fontWeight: 600 }}>{day.dateLabel}</div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>
                            {day.sessions.length} session{day.sessions.length !== 1 ? 's' : ''} • {day.totalMinutes} min
                          </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontWeight: 600, color: 'var(--color-primary)' }}>
                            €{day.totalEarnings.toFixed(2)}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>
                            {day.totalTips > 0 ? `(+€${day.totalTips.toFixed(2)} tips)` : ''} {expandedDay === day.date ? '▲' : '▼'}
                          </div>
                        </div>
                      </button>

                      {/* Expanded Sessions List */}
                      {expandedDay === day.date && (
                        <div style={{ borderTop: '1px solid var(--color-border)', padding: '0.5rem' }}>
                          {day.sessions.map((session) => (
                            <div
                              key={session.id}
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '0.5rem',
                                fontSize: '0.875rem',
                                borderBottom: '1px solid var(--color-border)',
                              }}
                            >
                              <div>
                                <div style={{ color: 'var(--color-text-secondary)' }}>
                                  {session.startedAt?.toDate?.()
                                    ? session.startedAt.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                                    : '-'}
                                </div>
                                <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>
                                  Table {session.tableNo}, Seat {session.seatNo}
                                </div>
                              </div>
                              <div style={{ textAlign: 'right' }}>
                                <div>{session.actualMinutes || session.totalMinutes} min</div>
                                <div style={{ color: 'var(--color-primary)', fontWeight: 500 }}>
                                  €{(session.therapistTotalPayout || 0).toFixed(2)}
                                  {(session.therapistTipPayout || 0) > 0 && (
                                    <span style={{ fontSize: '0.7rem', color: 'var(--color-text-secondary)' }}>
                                      {' '}(+€{(session.therapistTipPayout || 0).toFixed(2)})
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}

                          {/* Daily Summary and Settlement */}
                          <div style={{
                            marginTop: '0.5rem',
                            padding: '0.75rem',
                            backgroundColor: 'rgba(0,0,0,0.05)',
                            borderRadius: '6px',
                          }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
                              <span>Cash collected:</span>
                              <span style={{ fontWeight: 600 }}>€{day.totalAmountReceived.toFixed(2)}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
                              <span>Your earnings:</span>
                              <span style={{ fontWeight: 600, color: 'var(--color-primary)' }}>€{day.totalEarnings.toFixed(2)}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.75rem', fontSize: '0.875rem', fontWeight: 600 }}>
                              <span>To pay event:</span>
                              <span style={{ color: '#f59e0b' }}>€{day.eventPayout.toFixed(2)}</span>
                            </div>

                            {/* Settlement Status/Button */}
                            {settlements[day.date] ? (
                              <div style={{
                                padding: '0.5rem',
                                borderRadius: '6px',
                                backgroundColor: settlements[day.date].status === 'confirmed' ? 'rgba(34, 197, 94, 0.1)' : 'rgba(245, 158, 11, 0.1)',
                                border: `1px solid ${settlements[day.date].status === 'confirmed' ? '#22c55e' : '#f59e0b'}`,
                                textAlign: 'center',
                              }}>
                                <div style={{
                                  fontWeight: 600,
                                  color: settlements[day.date].status === 'confirmed' ? '#22c55e' : '#f59e0b',
                                  textTransform: 'uppercase',
                                  fontSize: '0.75rem',
                                }}>
                                  {settlements[day.date].status === 'confirmed' ? '✓ Settled' : '⏳ Pending Confirmation'}
                                </div>
                              </div>
                            ) : (
                              <button
                                onClick={() => createSettlement(day)}
                                disabled={settlingDay === day.date}
                                style={{
                                  width: '100%',
                                  padding: '0.75rem',
                                  backgroundColor: '#f59e0b',
                                  color: '#fff',
                                  border: 'none',
                                  borderRadius: '6px',
                                  fontWeight: 600,
                                  cursor: 'pointer',
                                  opacity: settlingDay === day.date ? 0.7 : 1,
                                }}
                              >
                                {settlingDay === day.date ? 'Creating...' : `Settle €${day.eventPayout.toFixed(2)}`}
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Grand Total */}
                <div
                  style={{
                    marginTop: '1rem',
                    padding: '1rem',
                    borderRadius: '8px',
                    backgroundColor: 'var(--color-primary)',
                    color: '#fff',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '1rem' }}>Total Earnings</div>
                      <div style={{ fontSize: '0.75rem', opacity: 0.9 }}>
                        {sessionsByDay.reduce((sum, d) => sum + d.sessions.length, 0)} sessions • {sessionsByDay.reduce((sum, d) => sum + d.totalMinutes, 0)} min
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontWeight: 700, fontSize: '1.25rem' }}>
                        €{sessionsByDay.reduce((sum, d) => sum + d.totalEarnings, 0).toFixed(2)}
                      </div>
                      {sessionsByDay.reduce((sum, d) => sum + d.totalTips, 0) > 0 && (
                        <div style={{ fontSize: '0.75rem', opacity: 0.9 }}>
                          incl. €{sessionsByDay.reduce((sum, d) => sum + d.totalTips, 0).toFixed(2)} tips
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </section>

      {/* Join New Event Section */}
      <section style={{ marginTop: '2rem', paddingTop: '1.5rem', borderTop: '1px solid var(--color-border)' }}>
        <h2 style={{ marginBottom: '0.5rem', fontSize: '1.25rem' }}>Join New Event</h2>
        <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.875rem', marginBottom: '1rem' }}>
          Have an invite code for a new festival? Enter it below.
        </p>

        {inviteSuccess ? (
          <div style={{
            padding: '1rem',
            borderRadius: '8px',
            backgroundColor: 'rgba(34, 197, 94, 0.1)',
            border: '1px solid var(--color-primary)',
            textAlign: 'center',
          }}>
            <div style={{ color: 'var(--color-primary)', fontWeight: 600, marginBottom: '0.5rem' }}>
              Successfully joined!
            </div>
            <p style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
              Refreshing...
            </p>
          </div>
        ) : (
          <form onSubmit={handleRedeemCode} style={{ display: 'flex', gap: '0.5rem' }}>
            <input
              type="text"
              value={inviteCode}
              onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
              placeholder="Enter invite code"
              maxLength={10}
              style={{
                flex: 1,
                padding: '0.75rem 1rem',
                fontSize: '1rem',
                textAlign: 'center',
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
              }}
            />
            <button
              type="submit"
              className="btn btn-primary"
              disabled={inviteLoading || !inviteCode.trim()}
            >
              {inviteLoading ? 'Joining...' : 'Join'}
            </button>
          </form>
        )}
        {inviteError && (
          <div style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.5rem' }}>
            {inviteError}
          </div>
        )}
      </section>
    </div>
  );
}
